import jmespath from "jmespath";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  parseFormattedCurrency,
  parseFormattedNumber,
  parseFormattedPercentage,
} from "../../../utils/formatNumber";
import { trellisPalette } from "../analytics/palettes";
import { filterRanges, generateFilterMarkup, generateRanges } from "../../../utils/rangeDropdownFilter";
import { Link } from "@tanstack/react-router";
import generateLink from "../../../utils/generateLink";
import { linkOptionsFromHref } from "../../../utils/searchParams";
import { CustomTriggerTooltip } from "../../core/basic/Tooltip";

const MediaPlanCell = (props) => {
  return (
    <div style={{ display: "flex" }}>
      <div>{props?.value}</div>
    </div>
  );
};

const col = (header, type, key, options) => ({
  header,
  type,
  key,
  options,
});

const tableLink = (link, linkText) => {
  if (typeof link === "object") {
    const { type, details, handleClick, productIdentifier, styles, openInNewTab, customText } = link ?? {};

    return <>{generateLink(type, details, handleClick, productIdentifier, styles, openInNewTab, customText)}</>;
  }

  if (typeof link === "string") {
    return <Link {...linkOptionsFromHref(link)}>{linkText}</Link>;
  }
  return;
};

// TODO remove the need for a categoryId prop once we have removed the old routing system
const makeColumn =
  (data, marketPlace, mobileStyle, date, setIsExpanded, categoryId, dateFormatter) => (header, key, type, options) => {
    const defaultAccessor = (p) => jmespath.search(p, key);
    const accessor = options?.accessor ?? defaultAccessor;
    const defaultFormat = ({ value }) => value;
    const fmt =
      options?.formatter ??
      {
        currency: ({ value }) =>
          formatCurrency(value, {
            marketPlace: marketPlace.marketPlace,
          }),
        number: ({ value }) => formatNumber(value, { decimalPlaces: 1 }),
        percent: ({ value }) =>
          formatPercent(value, {
            decimalPlaces: 0,
          }),
        date: ({ value }) => (value == null ? "" : dateFormatter.format(new Date(value))),
        advertisement: MediaPlanCell,
      }?.[type] ??
      defaultFormat;
    const parser =
      {
        currency: parseFormattedCurrency,
        number: parseFormattedNumber,
        percent: parseFormattedPercentage,
      }?.[type] ?? defaultFormat;

    let width;
    if (mobileStyle) {
      width = options?.mobileWidth;
    } else {
      width = options?.width;
    }

    return {
      Header: header,
      id: key,
      accessor,
      maxWidth: options?.maxWidth,
      minWidth: options?.minWidth,
      width,
      height: 30,
      className: options?.className ?? "text-end",
      headerClassName: options?.className ?? "text-end",
      Cell: (props) => {
        if (options?.showDot ?? true) {
          const percentKey = `${key}_percent`;
          const percent = props.original?.[percentKey] ?? null;
          const deltaKey = `${key}_delta`;
          const deltaValue = props.original?.[deltaKey];
          const isNegative = options?.isNegative ?? false;
          const noColor = options?.noColor ?? false;
          let link = options?.link ?? null;
          let linkText = options?.linkText ?? null;
          let color = "#666";
          if (!noColor && percent > 0) {
            color = isNegative ? trellisPalette[17] : trellisPalette[12];
          }
          if (!noColor && percent < 0) {
            color = isNegative ? trellisPalette[12] : trellisPalette[17];
          }

          if (options?.linkTextAccessor) {
            linkText = props.original?.[options?.linkTextAccessor];
          }

          if (options?.linkIdAccessor && typeof link === "string") {
            link = `${link}${props.original[options?.linkIdAccessor]}`;
          }

          return (
            <div>
              <CustomTriggerTooltip
                toolTip={
                  typeof link === "object" && link !== null ? (link.customText ?? props.original?.[link.textAccessor])
                  : deltaValue > 0 ?
                    "+"
                  : "" + fmt({ value: deltaValue })
                }
                placement="top"
              >
                <span>
                  {link ?
                    tableLink(
                      // Handle location prop/state based links with util
                      typeof link === "object" ?
                        {
                          ...link,
                          details: {
                            ...props.original,
                            category: categoryId,
                            // Below is a bit confusing, but the name stored in props is actually a string on the ad id, needed to send in location state
                            id: Number.parseInt(props.original.name),
                          },
                          customText: link.customText ?? props.original?.[link.textAccessor] ?? null,
                        }
                        // Simple URL link
                      : link,
                      linkText,
                    )
                  : fmt({ ...props, date })}
                </span>
              </CustomTriggerTooltip>
              <br />
              <span
                style={{
                  color,
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                }}
              >
                {percent === null ?
                  ""
                : formatPercent(percent, {
                    decimalPlaces: 0,
                    addPlus: true,
                  })
                }
              </span>
            </div>
          );
        }
        return fmt({ ...props, date });
      },

      style: { height: "60px" },
      filterMethod: (filter, row) => {
        const value = accessor(row._original);
        return filterRanges(filter, value, parser, marketPlace.marketPlace);
      },
      Filter: ({ filter, onChange }) => {
        return generateFilterMarkup(filter, onChange, key, "range", type, {
          ranges: generateRanges(data ?? [], 5, key, type, marketPlace.marketPlace),
          marketPlace: marketPlace.marketPlace,
        });
      },
    };
  };

export { makeColumn, col };
